import { queries, UnwrappedTypedGqlResult } from "@examspark/api";
import { filterMatches } from "@examspark/common";
import {
  Objective,
  ObjectiveTree,
  TeksCategory,
  TeksCurriculumUsage,
} from "@examspark/types";

import {
  getObjectiveCategory,
  getObjectiveCurriculumUsage,
  getObjectiveType,
} from "./utils";

export type CourseVerification = UnwrappedTypedGqlResult<
  typeof queries.course.getCourseVerification
>;

export type VerificationRowView =
  | {
      type: "scope";
      objective: Pick<Objective, "id" | "sk" | "content">;
    }
  | {
      type: "knowledge_statement";
      objective: Pick<Objective, "id" | "sk" | "abbreviation" | "content">;
    }
  | {
      type: "standard_row";
      objective: Pick<Objective, "id" | "sk" | "abbreviation" | "content">;
      category: TeksCategory | undefined;
      usages: Array<TeksCurriculumUsage | undefined>;
    };

export type VerificationView = {
  headers: Array<{ id: string; numberLabel: string | undefined }>;
  rows: VerificationRowView[];
  width: number;
};

export function createVerificationView(
  course: CourseVerification,
  objectivesSections: ObjectiveTree<Objective>[],
  filterText?: string,
  filterCategories?: TeksCategory[],
): VerificationView {
  const rows: VerificationRowView[] = [];

  for (const section of objectivesSections) {
    rows.push({
      type: "scope",
      objective: section.objective,
    });
    for (const child of section.children ?? []) {
      switch (getObjectiveType(child.objective)) {
        case "knowledge_statement":
          rows.push({
            type: "knowledge_statement",
            objective: child.objective,
          });
          break;
        case "student_expectation":
        case "romanette": {
          const category = getObjectiveCategory(child.objective);
          if (
            !filterMatches(
              filterText,
              child.objective.abbreviation,
              child.objective.content,
              category,
            )
          ) {
            break;
          }

          if (
            filterCategories?.length &&
            (category == null || !filterCategories.includes(category))
          ) {
            break;
          }

          const isSubHeading =
            child.objective.outlineNumber &&
            (/^[A-Z]\.$/.test(child.objective.outlineNumber) ||
              /^[1-9]\.$/.test(child.objective.outlineNumber));

          const isPreK = child.objective.abbreviation?.startsWith("PK");

          if (isPreK && isSubHeading) {
            rows.push({
              type: "knowledge_statement",
              objective: child.objective,
            });
            break;
          }

          rows.push({
            type: "standard_row",
            objective: child.objective,
            category,
            usages: course.units?.map((u) =>
              getObjectiveCurriculumUsage(
                u.unit.objectives?.find((o) => o.id === child.objective.id),
              ),
            ),
          });
          break;
        }
      }
    }
  }

  return {
    headers: course.units.map((u) => ({
      id: u.unit.id,
      numberLabel: u.unit.numberLabel ?? undefined,
    })),
    rows,
    width: course.units.length,
  };
}
